// Copyright (c) 2023 MOCA Systems, Inc. All rights reserved.

import * as appStrings from '@touchplan/strings';
import { CommonModule } from '@angular/common';
import { Observable, combineLatest, EMPTY, Subject, from } from 'rxjs';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { catchError, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { ChangeDetectionStrategy, Component, OnDestroy } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

import { AuthService } from 'src/app/services/auth.service';
import { MaterialModule } from 'src/app/common/material/material-module';
import { PresentationModule } from 'src/app/common/presentation-components/presentation.module';
import { AccountCreationStore } from 'src/app/common/auth/services/account-creation-store.service';
import { BannerMessageHandler } from '../../../logging/banner-message-handler';
import { analyticsEventsService } from 'src/app/plain-javascript/services/analytics/AnalyticsEventsService';
import { AuthOutputs } from '../../services/auth-outputs.service';
import { RouteUtils } from 'src/app/services/route-utils.service';
import { FirebaseError } from 'firebase/app';

const MODULES = [CommonModule, RouterModule, MaterialModule, PresentationModule, ReactiveFormsModule];

export enum DisplayLoginType {
	GOOGLE_SSO = 'GOOGLE_SSO',
	ONLY_PASSWORD = 'ONLY_PASSWORD',
}

const WRONG_PASSWORD_ERROR_CODE = 'auth/wrong-password';

@Component({
	selector: 'app-login2',
	standalone: true,
	imports: [...MODULES],
	providers: [BannerMessageHandler],
	templateUrl: './login2.component.html',
	styleUrls: ['./login2.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Login2Component implements OnDestroy {
	private wrongPasswordTries = 0;
	protected appStrings = appStrings;
	private destroy$ = new Subject<void>();

	public signInForm = this.formBuilder.group({
		password: ['', [Validators.required]],
	});

	public isSsoUser$: Observable<boolean> = this.accountCreationStore.isSSO$;

	public loginType$: Observable<DisplayLoginType> = combineLatest([
		this.accountCreationStore.email$,
		this.isSsoUser$,
	]).pipe(
		take(1),
		map(([email, isSsoUser]) =>
			email && !isSsoUser ? DisplayLoginType.ONLY_PASSWORD : DisplayLoginType.GOOGLE_SSO
		)
	);

	public onLoginWithEmailandPassword() {
		if (!this.signInForm.valid) {
			return;
		}
		this.accountCreationStore.setLoadingState(true);

		const signInFormData = this.signInForm.getRawValue();
		this.accountCreationStore.updatePassword(signInFormData.password);
		this.accountCreationStore.accountData$
			.pipe(
				take(1),
				switchMap(({ email, password }) =>
					from(this.authService.signInWithEmailAndPassword(email, password))
				),
				tap({
					next: (user) => {
						analyticsEventsService.login(user.user.uid);
						this.accountCreationStore.setLoadingState(false);
					},
					error: () => {
						this.accountCreationStore.setLoadingState(false);
					},
				}),
				catchError((error: FirebaseError) => {
					if (error.code === WRONG_PASSWORD_ERROR_CODE) {
						this.wrongPasswordTries += 1;

						if (this.wrongPasswordTries >= 3) {
							this.bannerMessageHandler.displayWarningMessage(
								appStrings.BANNER_MESSAGE_REPEATED_LOGIN_WARNING_MESSAGE
							);
							return EMPTY;
						}
					} else {
						this.wrongPasswordTries = 0;
					}

					this.bannerMessageHandler.displayErrorMessage(error);
					return EMPTY;
				}),
				takeUntil(this.destroy$)
			)
			.subscribe(() => {
				const redirect = this.routeUtils.userAttemptedUrl;
				this.routeUtils.userAttemptedUrl = null;
				this.authOutputs.finishLoginWithEmailAndPassword(redirect);
			});
	}

	public onForgotPassword() {
		this.authOutputs.forgotPasswordRedirect(this.activatedRoute);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	constructor(
		private formBuilder: FormBuilder,
		private authService: AuthService,
		private activatedRoute: ActivatedRoute,
		private accountCreationStore: AccountCreationStore,
		private bannerMessageHandler: BannerMessageHandler,
		private authOutputs: AuthOutputs,
		private routeUtils: RouteUtils
	) {}
}
